import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import type { AccountMetrics } from '../types';

interface MetricsGridProps {
  metrics: AccountMetrics;
  onNavigateToTab?: (tab: string) => void;
}

export default function MetricsGrid({ metrics, onNavigateToTab }: MetricsGridProps) {
  return (
    <>
      <MetricCard
        title="ARR"
        value={formatCurrency(metrics.arr)}
        subtitle={`MRR: ${formatCurrency(metrics.mrr)}`}
        tooltip="Annual Recurring Revenue from active contracts. MRR = ARR / 12."
      />
      <MetricCard
        title="Sensors"
        value={metrics.total_sensors.toString()}
        subtitle={`${metrics.active_sensors} active, ${metrics.inactive_sensors} inactive`}
        valueColor={metrics.inactive_sensors > 0 ? 'text-yellow-600' : undefined}
        onClick={() => onNavigateToTab?.('sensors')}
        clickable
        tooltip="Total non-deleted sensors for this account. Inactive is status 5 only; unmanaged, maintenance, inhibited, warning, and archived are separate statuses."
      />
      <MetricCard
        title="Open Tickets"
        value={metrics.open_tickets.toString()}
        subtitle={
          metrics.critical_tickets > 0
            ? `${metrics.critical_tickets} critical`
            : 'No critical'
        }
        valueColor={metrics.critical_tickets > 0 ? 'text-red-600' : undefined}
        onClick={() => onNavigateToTab?.('tickets')}
        clickable
        tooltip="Tickets with status Pending or Waiting. Critical = priority score >= 20 (important x urgent)."
      />
    </>
  );
}

interface MetricCardProps {
  title: string;
  value: string;
  subtitle: string;
  tooltip?: string;
  valueColor?: string;
  onClick?: () => void;
  clickable?: boolean;
}

function MetricCard({ title, value, subtitle, tooltip, valueColor, onClick, clickable }: MetricCardProps) {
  return (
    <Card
      className={clickable ? 'cursor-pointer transition-colors hover:bg-muted/50' : ''}
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
          {title}
          {tooltip && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm"
                    aria-label={`Info about ${title}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Info className="h-3.5 w-3.5 text-muted-foreground/60 hover:text-muted-foreground cursor-help" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-xs">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${valueColor || ''}`}>{value}</div>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </CardContent>
    </Card>
  );
}
